import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { trapFocus, removeTrapFocus } from '@theme/focus';

/**
 * `<pv-product-gallery>` — PokerVault product media gallery.
 *
 * Single large main image + thin left thumbnail rail (when >1 media).
 * Hover-reveal prev/next chevrons (disabled at bounds) + fullscreen icon.
 * Clicking the main image does NOT open fullscreen; only the fullscreen
 * icon does.
 *
 * State: one integer — the currently-selected media index. The same index
 * drives both the main stage and the fullscreen dialog, so opening the
 * dialog picks up where the stage left off and closing it leaves the
 * stage in sync.
 *
 * Variant handshake: when the variant picker dispatches
 * `ThemeEvents.variantUpdate`, the payload includes a re-rendered section
 * HTML. We swap ourselves out with the fresh `<pv-product-gallery>` from
 * that HTML — same pattern as Horizon's `<media-gallery>` does.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} stage
 * @property {HTMLElement[]} [slide]
 * @property {HTMLButtonElement[]} [thumbnail]
 * @property {HTMLButtonElement} [prev]
 * @property {HTMLButtonElement} [next]
 * @property {HTMLButtonElement} fullscreenBtn
 * @property {import('@theme/dialog').DialogComponent} fullscreenDialog
 *
 * @extends Component<Refs>
 */
class PvProductGallery extends Component {
  #currentIndex = 0;
  /** @type {number} — max index; -1 when no media. */
  #maxIndex = 0;
  /** @type {boolean} */
  #dialogOpen = false;

  // Refs inside <dialog-component> never reach this.refs: Horizon's ref
  // collector treats every *-component tag as a Component boundary
  // (see getClosestComponent in component.js), so `dialog`, `dialogPrev`,
  // `dialogNext`, `counterCurrent`, `dialogSlide[]` etc. get claimed by
  // the inner <dialog-component> instead of bubbling up to us. We resolve
  // them with direct querySelector calls.
  /** @type {HTMLDialogElement | null} */
  #dialogEl = null;
  /** @type {HTMLButtonElement | null} */
  #dialogPrevEl = null;
  /** @type {HTMLButtonElement | null} */
  #dialogNextEl = null;
  /** @type {HTMLElement | null} */
  #counterCurrentEl = null;
  /** @type {HTMLElement[]} */
  #dialogSlideEls = [];

  connectedCallback() {
    super.connectedCallback();

    const slides = this.#slides;
    this.#maxIndex = slides.length - 1;

    // Server renders the first slide visible; mirror that in state.
    this.#currentIndex = 0;

    this.#resolveDialogElements();
    this.#syncState();

    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.refs.fullscreenDialog?.addEventListener('dialog:close', this.#onDialogClose);

    this.#dialogPrevEl?.addEventListener('click', this.prev);
    this.#dialogNextEl?.addEventListener('click', this.next);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    document.removeEventListener('keydown', this.#handleDialogKeyDown);
    this.refs.fullscreenDialog?.removeEventListener('dialog:close', this.#onDialogClose);
    this.#dialogPrevEl?.removeEventListener('click', this.prev);
    this.#dialogNextEl?.removeEventListener('click', this.next);
  }

  #resolveDialogElements() {
    this.#dialogEl = this.querySelector('dialog[ref="dialog"]');
    this.#dialogPrevEl = this.querySelector('button[ref="dialogPrev"]');
    this.#dialogNextEl = this.querySelector('button[ref="dialogNext"]');
    this.#counterCurrentEl = this.querySelector('[ref="counterCurrent"]');
    this.#dialogSlideEls = Array.from(this.querySelectorAll('[ref="dialogSlide[]"]'));
  }

  // --- Ref accessors (normalize single-vs-array) ----------------------

  /** @returns {HTMLElement[]} */
  get #slides() {
    const ref = this.refs.slide;
    if (!ref) return [];
    return Array.isArray(ref) ? ref : [ref];
  }

  /** @returns {HTMLButtonElement[]} */
  get #thumbnails() {
    const ref = this.refs.thumbnail;
    if (!ref) return [];
    return Array.isArray(ref) ? ref : [ref];
  }

  // --- Public actions (bound to `on:click="/name"` handlers) ----------

  /**
   * Handle thumbnail click. Two modes:
   *   1. Color rail — thumbnail carries `data-option-value-id`. Flip the
   *      matching radio in `<variant-picker>` and let the standard
   *      variantUpdate flow re-render us. No local state change.
   *   2. Media rail — thumbnail carries `data-index`. Swap the active
   *      slide locally; no network round-trip.
   *
   * @param {MouseEvent} event
   */
  selectIndex = (event) => {
    // Horizon delegates on:click at document; event.target is the element
    // with the on:click attribute (the <button>), not the clicked child <img>.
    // currentTarget is document in delegated events — don't use it.
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const optionValueId = target.dataset.optionValueId;
    if (optionValueId) {
      // Optimistic: flip the active class on the rail so the click feels
      // instant. The full re-render that lands ~200ms later will rebuild
      // the rail and lock the correct state.
      this.#thumbnails.forEach((thumb) => {
        const active = thumb === target;
        thumb.classList.toggle('pv-gallery__thumbnail--active', active);
        thumb.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      this.#triggerVariantChange(optionValueId);
      return;
    }

    const index = Number.parseInt(target.dataset.index ?? '', 10);
    if (Number.isNaN(index)) return;
    this.#select(index);
  };

  /**
   * Find the matching radio inside the page's `<variant-picker>` for this
   * product and fire a native `change`. The picker's own listener picks it
   * up via the standard `change` bubble path and runs its full flow
   * (fetch new section HTML, dispatch VariantUpdateEvent), which then
   * re-renders this gallery via #onVariantUpdate.
   *
   * @param {string} optionValueId
   */
  #triggerVariantChange(optionValueId) {
    const productId = this.dataset.productId;
    const escapedId = CSS.escape(optionValueId);

    // Prefer the variant-picker bound to our product (combined-listing
    // pages can host multiple pickers). Fall back to any picker if we
    // can't find a product-scoped one.
    let radio = null;
    if (productId) {
      const escapedProductId = CSS.escape(productId);
      radio = document.querySelector(
        `variant-picker[data-product-id="${escapedProductId}"] input[data-option-value-id="${escapedId}"]`
      );
    }
    if (!radio) {
      radio = document.querySelector(`variant-picker input[data-option-value-id="${escapedId}"]`);
    }
    if (!(radio instanceof HTMLInputElement)) return;

    // Already selected — nothing to do.
    if (radio.checked) return;

    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
  }

  prev = () => {
    if (this.#currentIndex > 0) this.#select(this.#currentIndex - 1);
  };

  next = () => {
    if (this.#currentIndex < this.#maxIndex) this.#select(this.#currentIndex + 1);
  };

  openFullscreen = () => {
    const dialogComponent = this.refs.fullscreenDialog;
    if (!dialogComponent) return;

    dialogComponent.showDialog();
    this.#dialogOpen = true;

    // Sync dialog to the current index before trapping focus so the right
    // slide is visible when focus lands.
    this.#syncDialog();

    document.addEventListener('keydown', this.#handleDialogKeyDown);

    // Trap focus inside the dialog — first focusable inside should be the
    // close button (first in DOM order after the counter span).
    if (this.#dialogEl) trapFocus(this.#dialogEl);
  };

  closeDialog = () => {
    this.refs.fullscreenDialog?.closeDialog();
    // dialog:close event handler resolves the rest.
  };

  // --- Internals ------------------------------------------------------

  /** @param {KeyboardEvent} event */
  #handleDialogKeyDown = (event) => {
    if (!this.#dialogOpen) return;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.prev();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    }
  };

  #onDialogClose = () => {
    this.#dialogOpen = false;
    document.removeEventListener('keydown', this.#handleDialogKeyDown);
    removeTrapFocus();
    // Return focus to the fullscreen trigger so keyboard users don't lose place.
    this.refs.fullscreenBtn?.focus();
  };

  /** @param {number} index */
  #select(index) {
    if (this.#maxIndex < 0) return;
    const clamped = Math.max(0, Math.min(this.#maxIndex, index));
    if (clamped === this.#currentIndex) return;
    this.#currentIndex = clamped;
    this.#syncState();
  }

  #syncState() {
    const index = this.#currentIndex;

    // Main stage: show only the active slide.
    this.#slides.forEach((slide, i) => {
      slide.hidden = i !== index;
    });

    // Thumbnail highlighting + scroll-into-view.
    this.#thumbnails.forEach((thumb, i) => {
      const active = i === index;
      thumb.setAttribute('aria-selected', active ? 'true' : 'false');
      thumb.classList.toggle('pv-gallery__thumbnail--active', active);
      thumb.tabIndex = active ? 0 : -1;
      if (active) {
        thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      }
    });

    // Main-stage chevrons.
    if (this.refs.prev instanceof HTMLButtonElement) {
      this.refs.prev.disabled = index === 0;
    }
    if (this.refs.next instanceof HTMLButtonElement) {
      this.refs.next.disabled = index >= this.#maxIndex;
    }

    // Mirror state into the open dialog.
    if (this.#dialogOpen) this.#syncDialog();
  }

  #syncDialog() {
    const index = this.#currentIndex;

    this.#dialogSlideEls.forEach((slide, i) => {
      slide.hidden = i !== index;
    });

    if (this.#counterCurrentEl) {
      this.#counterCurrentEl.textContent = String(index + 1);
    }

    if (this.#dialogPrevEl) {
      this.#dialogPrevEl.disabled = index === 0;
    }
    if (this.#dialogNextEl) {
      this.#dialogNextEl.disabled = index >= this.#maxIndex;
    }
  }

  /** @param {Event} event */
  #onVariantUpdate = (event) => {
    const detail = /** @type {{ data?: { html?: Document | string; productId?: string } }} */ (
      /** @type {CustomEvent} */ (event).detail ?? {}
    );
    const source = detail.data?.html;
    if (!source) return;

    // variant-picker.js dispatches `html` as a parsed Document (DOMParser
    // result is cached and re-used). Mirror Horizon's <media-gallery>
    // handler and use it directly; only parse if a future caller passes
    // a string.
    const doc = typeof source === 'string'
      ? new DOMParser().parseFromString(source, 'text/html')
      : source;

    // Combined-listing safety: a single page can host multiple galleries
    // (one per product card / featured product). Only react to events for
    // our product.
    const eventProductId = detail.data?.productId;
    const myProductId = this.dataset.productId;
    if (eventProductId && myProductId && eventProductId !== myProductId) return;

    const selector = myProductId
      ? `pv-product-gallery[data-product-id="${CSS.escape(myProductId)}"]`
      : 'pv-product-gallery';
    const fresh = doc.querySelector(selector);
    if (fresh) this.replaceWith(fresh);
  };
}

if (!customElements.get('pv-product-gallery')) {
  customElements.define('pv-product-gallery', PvProductGallery);
}
